import IMask from 'imask';

import { MessageService } from '@philips-emr/tws-core/lib/services';

import { getLocale, NumberFormat } from '../../locale';

import { NumericFormatterOptions, NumericMaskData } from './numeric-type';

function getLocaleNumberFormat(): NumberFormat {
  const { NUMBER_FORMAT } = getLocale(MessageService.getCurrentLanguage());
  return NUMBER_FORMAT;
}

function mergeDefaults(options: NumericFormatterOptions = {}, defaults: NumericFormatterOptions = {}): Required<NumericFormatterOptions> {
  const { DECIMAL_SEPARATOR, GROUP_SEPARATOR } = getLocaleNumberFormat();
  const {
    groupSize = 3,
    padFractionalZeros = false,
    radix = DECIMAL_SEPARATOR,
    scale = 3,
    thousandsSeparator = GROUP_SEPARATOR,
  } = defaults;

  return {
    groupSize,
    padFractionalZeros,
    radix,
    scale,
    thousandsSeparator,
    ...options,
  };
}

function getMaskOptions(options: NumericFormatterOptions, defaults: NumericFormatterOptions = {}): IMask.MaskedNumberOptions & Required<NumericFormatterOptions> {
  const { groupSize, padFractionalZeros, radix, scale, thousandsSeparator } = mergeDefaults(options, defaults);

  return {
    groupSize,
    mapToRadix: [],
    mask: Number,
    normalizeZeros: false,
    padFractionalZeros,
    radix,
    scale,
    thousandsSeparator,
  };
}

function injectCustomMethods(MaskedNumber: typeof IMask.MaskedNumber, options: Required<NumericFormatterOptions>): void {
  const { groupSize, radix, thousandsSeparator } = options;

  //@ts-ignore
  MaskedNumber.prototype._insertThousandsSeparators = (value: string): string => {
    const parts = value.split(radix);
    const thousandsRegex = new RegExp(`\\B(?=(\\d{${groupSize}})+(?!\\d))`, 'g');
    parts[0] = parts[0].replace(thousandsRegex, thousandsSeparator);
    return parts.join(radix);
  };
}

function isNumberValid(value: string, options: NumericFormatterOptions): boolean {
  const { DECIMAL_SEPARATOR, GROUP_SEPARATOR } = getLocaleNumberFormat();
  const { thousandsSeparator = DECIMAL_SEPARATOR, radix = GROUP_SEPARATOR } = options;
  return !value.endsWith(thousandsSeparator) && !value.endsWith(radix) && !isNaN(Number(value));
}

function removePrefix(value: string, shouldRemove: boolean = true): string {
  const { CURRENCY_SYMBOL } = getLocaleNumberFormat();
  return shouldRemove ? value.replace(`${CURRENCY_SYMBOL} `, '') : value;
}

function addPrefix(value: string, shouldAdd: boolean = true): string {
  const { CURRENCY_SYMBOL } = getLocaleNumberFormat();
  return shouldAdd ? `${CURRENCY_SYMBOL} ${value}` : value;
}

function createMask(options: NumericFormatterOptions, defaults: NumericFormatterOptions = {}): NumericMaskData {
  const maskOptions = getMaskOptions(options, defaults);

  injectCustomMethods(IMask.MaskedNumber, maskOptions);

  const masked = new IMask.MaskedNumber(maskOptions);

  return { masked, options: maskOptions };
}

export {
  addPrefix,
  createMask,
  getLocaleNumberFormat,
  getMaskOptions,
  injectCustomMethods,
  isNumberValid,
  mergeDefaults,
  removePrefix,
};
